import { useEffect, useRef, useCallback } from "react";
import { getLiveUserPointer } from "@/components/cursors/user-pointer-tracker";
import {
  cursorDriftOffset,
  dampPoint,
  easeInOutCubic,
  FLUID_POINTER_DAMPING_SECONDS,
  getIdleScatterOffset,
  penTravel,
  penTravelControls,
  penTravelPoint,
  type PenTravelPath,
} from "@/components/cursors/cursor-animation-geometry";
import { frameRestingCursorAnchor } from "@/components/cursors/frame-cursor-anchor";
import { frameWorkingStartCursorAnchor } from "@/components/cursors/frame-cursor-geometry";
import { frameScreenRect } from "@/lib/frame-screen-rect";
import { notifyCursorAnimationComplete } from "@/lib/cursor-animation-completion";
import {
  DEFAULT_TRAVEL_DURATION_MS,
  type CursorMode,
  type UseCursorAnimationOptions,
} from "@/components/cursors/cursor-animation-types";

export { getIdleScatterOffset } from "@/components/cursors/cursor-animation-geometry";
export {
  DEFAULT_TRAVEL_DURATION_MS,
  type CursorAnimationState,
  type CursorMode,
} from "@/components/cursors/cursor-animation-types";
import { prefersReducedMotion } from "@/lib/reveal-motion";

const FOLLOW_POINTER_OFFSET_X = 24;
const FOLLOW_POINTER_OFFSET_Y = 24;

const IDLE_DRIFT_SPEED = 0.015;

const FRAME_DRIFT_PADDING = 20;

const WORKING_STAGE_MS = 1500;
const WORKING_DRIFT_SPEED = 0.8;

/**
 * Drives cursor position via requestAnimationFrame.
 * Modes: idle drift (by ticker), bezier travel, staged working sequence, resting outside frame.
 */
export function useCursorAnimation({
  targetFrameId,
  actionSeq = null,
  onUpdate,
  bandIndex = 0,
  spawnAnchor = null,
  followLivePointer = false,
  freezePosition = false,
  ambientMotion = true,
  passiveAnchor = null,
  pointTarget = null,
  pointSeq = null,
  travelDurationMs = DEFAULT_TRAVEL_DURATION_MS,
  completionToken,
}: UseCursorAnimationOptions) {
  const rafId = useRef<number>(0);
  const modeRef = useRef<CursorMode>("idle");
  const posRef = useRef({ x: 0, y: 0 });
  const hasPositionRef = useRef(false);
  const lastFrameTimeRef = useRef(0);
  const prevTargetRef = useRef<string | null>(null);
  const prevActionSeqRef = useRef<number | null>(null);
  const prevPointSeqRef = useRef<number | null>(null);
  const pendingFrameTravelRef = useRef<{ frameId: string } | null>(null);
  const angleRef = useRef(Math.random() * Math.PI * 2);

  const spawnAnchorRef = useRef(spawnAnchor);
  spawnAnchorRef.current = spawnAnchor;
  const passiveAnchorRef = useRef(passiveAnchor);
  passiveAnchorRef.current = passiveAnchor;

  // Live pointer following ignores submit-time pins; those only seed spawn
  // anchoring. When following stops, freeze at the current pointer so replies
  // stay readable.
  const followLivePointerRef = useRef(followLivePointer);
  const frozenAnchorRef = useRef<{ x: number; y: number } | null>(null);
  if (followLivePointerRef.current && !followLivePointer) {
    const p = getLiveUserPointer();
    frozenAnchorRef.current = p
      ? {
          x: p.x + FOLLOW_POINTER_OFFSET_X,
          y: p.y + FOLLOW_POINTER_OFFSET_Y,
        }
      : null;
  } else if (followLivePointer) {
    frozenAnchorRef.current = null;
  }
  followLivePointerRef.current = followLivePointer;

  const freezePositionRef = useRef(freezePosition);
  const ambientMotionRef = useRef(ambientMotion);
  ambientMotionRef.current = ambientMotion;
  const lockedPositionRef = useRef<{ x: number; y: number } | null>(null);
  if (!freezePosition) {
    lockedPositionRef.current = null;
  } else if (!freezePositionRef.current && hasPositionRef.current) {
    lockedPositionRef.current = { ...posRef.current };
  }
  freezePositionRef.current = freezePosition;

  const bandIndexRef = useRef(bandIndex);
  bandIndexRef.current = bandIndex;

  const travelRef = useRef<
    | (PenTravelPath & {
        startTime: number;
        onComplete: CursorMode;
        completionToken?: string;
      })
    | null
  >(null);

  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const targetFrameIdRef = useRef(targetFrameId);
  targetFrameIdRef.current = targetFrameId;
  const pointTargetRef = useRef(pointTarget);
  pointTargetRef.current = pointTarget;
  const travelDurationRef = useRef(travelDurationMs);
  travelDurationRef.current = travelDurationMs;

  const stageStartRef = useRef(0);

  /** Idle anchor fallback when no spawn anchor was captured (e.g. the
   *  user's pointer was never inside the viewport since page load).
   *  Uses the old activity-ticker position — bottom-left, unobtrusive,
   *  doesn't cover canvas content. */
  const getFallbackAnchor = useCallback((): { x: number; y: number } => {
    return { x: 280, y: window.innerHeight - 90 };
  }, []);

  /** Start a bowed travel from current pos to destination */
  const startTravel = useCallback(
    (
      end: { x: number; y: number },
      onComplete: CursorMode,
      nextCompletionToken?: string,
    ) => {
      travelRef.current = {
        ...penTravel({ ...posRef.current }, end, nextCompletionToken),
        startTime: performance.now(),
        onComplete,
        completionToken: nextCompletionToken,
      };
      modeRef.current = "travel";
    },
    [],
  );

  const startFrameTravel = useCallback(
    (frameId: string): boolean => {
      const rect = frameScreenRect(frameId);
      if (!rect) return false;
      startTravel(frameWorkingStartCursorAnchor(rect), "frame-working-start");
      return true;
    },
    [startTravel],
  );

  // Main animation loop — runs perpetually, reads targetFrameId via ref
  useEffect(() => {
    function tick() {
      const now = performance.now();
      const deltaMs = lastFrameTimeRef.current
        ? now - lastFrameTimeRef.current
        : 0;
      lastFrameTimeRef.current = now;
      const pendingFrameTravel = pendingFrameTravelRef.current;
      if (
        pendingFrameTravel &&
        pendingFrameTravel.frameId === targetFrameIdRef.current &&
        startFrameTravel(pendingFrameTravel.frameId)
      ) {
        pendingFrameTravelRef.current = null;
      }
      const mode = modeRef.current;

      if (mode === "idle") {
        if (freezePositionRef.current && lockedPositionRef.current) {
          posRef.current = lockedPositionRef.current;
          onUpdateRef.current({
            x: posRef.current.x,
            y: posRef.current.y,
            mode,
          });
          rafId.current = requestAnimationFrame(tick);
          return;
        }
        // Idle anchor priority:
        //   1. Live pointer (while the agent is "waking up" / thinking)
        //   2. Frozen pointer (snapshot taken when follow flipped off)
        //   3. Passive anchor (dock mode)
        //   4. Spawn anchor (captured at first appearance)
        //   5. Fallback corner (no pointer ever observed)
        let anchor: { x: number; y: number } | null = null;
        if (followLivePointerRef.current) {
          const p = getLiveUserPointer();
          if (p) {
            anchor = {
              x: p.x + FOLLOW_POINTER_OFFSET_X,
              y: p.y + FOLLOW_POINTER_OFFSET_Y,
            };
          }
        }
        anchor =
          anchor ??
          frozenAnchorRef.current ??
          passiveAnchorRef.current ??
          spawnAnchorRef.current ??
          getFallbackAnchor();

        // Parallel idle cursors fan out around the anchor. The offsets are
        // deterministic from bandIndex, so layout remains stable while several
        // agents follow the live user pointer.
        const scatter = getIdleScatterOffset(bandIndexRef.current);
        angleRef.current += IDLE_DRIFT_SPEED;
        const drift = cursorDriftOffset(
          angleRef.current,
          ambientMotionRef.current,
        );
        const target = {
          x: anchor.x + scatter.x + drift.x,
          y: anchor.y + scatter.y + drift.y,
        };

        posRef.current = hasPositionRef.current
          ? dampPoint(
              posRef.current,
              target,
              FLUID_POINTER_DAMPING_SECONDS,
              deltaMs,
            )
          : target;
        hasPositionRef.current = true;
        if (freezePositionRef.current) {
          lockedPositionRef.current = { ...posRef.current };
        }
      } else if (mode === "travel") {
        const travel = travelRef.current;
        if (travel) {
          const fid = targetFrameIdRef.current;
          const rect = fid ? frameScreenRect(fid) : null;
          const newEnd = rect
            ? travel.onComplete === "frame-resting"
              ? frameRestingCursorAnchor(rect)
              : {
                  x: rect.left + FRAME_DRIFT_PADDING + 20,
                  y: rect.top + FRAME_DRIFT_PADDING + 10,
                }
            : !fid && travel.onComplete === "point-resting"
              ? pointTargetRef.current
              : null;
          if (newEnd) {
            travel.end = newEnd;
            Object.assign(
              travel,
              penTravelControls(travel.start, newEnd, travel.side),
            );
          }
          const elapsed = performance.now() - travel.startTime;
          const duration = Math.max(0, travelDurationRef.current);
          const t = duration === 0 ? 1 : Math.min(elapsed / duration, 1);
          posRef.current = penTravelPoint(travel, easeInOutCubic(t));
          hasPositionRef.current = true;
          if (t >= 1) {
            if (travel.completionToken) {
              notifyCursorAnimationComplete(travel.completionToken);
            }
            modeRef.current = travel.onComplete;
            travelRef.current = null;
            stageStartRef.current = performance.now();
          }
        }
      } else if (
        mode === "frame-working-start" ||
        mode === "frame-working-mid" ||
        mode === "frame-working-end"
      ) {
        const fid = targetFrameIdRef.current;
        if (!fid) {
          modeRef.current = "idle";
          rafId.current = requestAnimationFrame(tick);
          return;
        }
        const rect = frameScreenRect(fid);
        if (!rect) {
          modeRef.current = "idle";
          rafId.current = requestAnimationFrame(tick);
          return;
        }

        // Compute stage target position
        let target: { x: number; y: number };
        if (mode === "frame-working-start") {
          target = {
            x: rect.left + FRAME_DRIFT_PADDING + 20,
            y: rect.top + FRAME_DRIFT_PADDING + 10,
          };
        } else if (mode === "frame-working-mid") {
          target = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          };
        } else {
          target = {
            x: rect.left + rect.width - FRAME_DRIFT_PADDING - 40,
            y: rect.top + rect.height - FRAME_DRIFT_PADDING - 20,
          };
        }

        // Drift toward target
        const dx = target.x - posRef.current.x;
        const dy = target.y - posRef.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 1) {
          posRef.current = {
            x: posRef.current.x + (dx / dist) * WORKING_DRIFT_SPEED,
            y: posRef.current.y + (dy / dist) * WORKING_DRIFT_SPEED,
          };
          hasPositionRef.current = true;
        }

        // Time-based stage advance
        const elapsed = performance.now() - stageStartRef.current;
        if (elapsed > WORKING_STAGE_MS) {
          if (mode === "frame-working-start") {
            modeRef.current = "frame-working-mid";
          } else if (mode === "frame-working-mid") {
            modeRef.current = "frame-working-end";
          } else {
            modeRef.current = "frame-resting";
          }
          stageStartRef.current = performance.now();
        }
      } else if (mode === "frame-resting") {
        const fid = targetFrameIdRef.current;
        if (!fid) {
          modeRef.current = "idle";
          rafId.current = requestAnimationFrame(tick);
          return;
        }
        const rect = frameScreenRect(fid);
        if (!rect) {
          modeRef.current = "idle";
          rafId.current = requestAnimationFrame(tick);
          return;
        }

        // Anchor at top-right; decorative drift is optional for status pills.
        const anchor = frameRestingCursorAnchor(rect);
        angleRef.current += IDLE_DRIFT_SPEED;
        const drift = cursorDriftOffset(
          angleRef.current,
          ambientMotionRef.current,
        );
        posRef.current = {
          x: anchor.x + drift.x,
          y: anchor.y + drift.y,
        };
        hasPositionRef.current = true;
      } else if (mode === "point-resting") {
        const anchor = pointTargetRef.current;
        if (!anchor) {
          modeRef.current = "idle";
          rafId.current = requestAnimationFrame(tick);
          return;
        }
        posRef.current = anchor;
        hasPositionRef.current = true;
      }

      onUpdateRef.current({
        x: posRef.current.x,
        y: posRef.current.y,
        mode: modeRef.current,
      });

      rafId.current = requestAnimationFrame(tick);
    }

    rafId.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId.current);
    // Empty dep array — loop runs perpetually, reads targetFrameId via ref.
    // Travel trigger effect (below) handles mode transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !pointTarget ||
      pointSeq === null ||
      pointSeq === prevPointSeqRef.current
    ) {
      return;
    }
    prevPointSeqRef.current = pointSeq;
    prevTargetRef.current = null;
    pendingFrameTravelRef.current = null;
    const shouldSnap =
      travelDurationRef.current === 0 || prefersReducedMotion();
    if (!hasPositionRef.current && !shouldSnap) {
      posRef.current =
        spawnAnchorRef.current ?? getLiveUserPointer() ?? getFallbackAnchor();
      hasPositionRef.current = true;
      startTravel(pointTarget, "point-resting", completionToken);
      onUpdateRef.current({
        x: posRef.current.x,
        y: posRef.current.y,
        mode: "travel",
      });
      return;
    }
    if (shouldSnap) {
      posRef.current = pointTarget;
      hasPositionRef.current = true;
      modeRef.current = "point-resting";
      travelRef.current = null;
      onUpdateRef.current({
        x: pointTarget.x,
        y: pointTarget.y,
        mode: "point-resting",
      });
      if (completionToken) notifyCursorAnimationComplete(completionToken);
      return;
    }
    startTravel(pointTarget, "point-resting", completionToken);
  }, [completionToken, getFallbackAnchor, pointTarget, pointSeq, startTravel]);

  // React to targetFrameId / actionSeq changes — trigger travel animations.
  // Always travel into the working sequence (start → mid → end → resting)
  // when newly targeting a frame OR when the agent fires another frame
  // action on the same frame (action_seq bumped). The latter lets a
  // sequence of frame.update or frame.read calls each pulse the cursor
  // even though target_frame_id never changes.
  useEffect(() => {
    const targetChanged = targetFrameId !== prevTargetRef.current;
    const actionChanged =
      actionSeq !== null && actionSeq !== prevActionSeqRef.current;
    if (!targetChanged && !actionChanged) return;

    const prev = prevTargetRef.current;
    prevTargetRef.current = targetFrameId;
    prevActionSeqRef.current = actionSeq;
    pendingFrameTravelRef.current = null;

    if (targetFrameId) {
      if (!startFrameTravel(targetFrameId)) {
        pendingFrameTravelRef.current = { frameId: targetFrameId };
      }
    } else if (prev) {
      // Returning to idle: travel back to the most relevant idle anchor.
      // Prefer the frozen anchor (set when the reply locked in), then
      // dock anchor, then spawn anchor, then fallback. Don't dip into
      // the live pointer here — the travel is a one-shot bezier, not a
      // continuous follow.
      const anchor =
        frozenAnchorRef.current ??
        passiveAnchorRef.current ??
        spawnAnchorRef.current ??
        getFallbackAnchor();
      startTravel(anchor, "idle");
    }
  }, [
    targetFrameId,
    actionSeq,
    getFallbackAnchor,
    startFrameTravel,
    startTravel,
  ]);
}
